// Utility functions for managing cart data in localStorage
// This ensures reliable access to cart items for Stripe payments

export interface CartItem {
  product_id: string;
  quantity: number;
  price: number;
  name?: string;
  variation_id?: string;
  [key: string]: any;
}

export interface CartStorageData {
  items: CartItem[];
  timestamp: number;
  total: number;
  orderTotal: number;
}

export interface OrderData {
  cart: CartItem[];
  total: number;
  orderTotal: number;
  shippingTotal: number;
  vatAmount: number;
  customerName: string;
  customerEmail: string;
  storeInfo: any;
  paymentIntentId: string;
}

/**
 * Store cart items in localStorage with timestamp
 */
export const storeCartItems = (items: CartItem[], total: number, orderTotal: number): void => {
  try {
    const cartData: CartStorageData = {
      items,
      timestamp: Date.now(),
      total,
      orderTotal
    };
    localStorage.setItem('stripeCartItems', JSON.stringify(cartData));
    console.log('Cart items stored successfully:', items.length, 'items');
  } catch (error) {
    console.error('Failed to store cart items:', error);
  }
};

/**
 * Retrieve cart items from localStorage with fallback
 */
export const getCartItems = (): CartItem[] | null => {
  try {
    // Try to get from dedicated cart storage first
    const savedCartItems = localStorage.getItem('stripeCartItems');
    if (savedCartItems) {
      const cartData: CartStorageData = JSON.parse(savedCartItems);
      if (cartData.items && Array.isArray(cartData.items) && cartData.items.length > 0) {
        // Check if data is not too old (1 hour max)
        const isDataFresh = Date.now() - cartData.timestamp < 60 * 60 * 1000;
        if (isDataFresh) {
          console.log('Retrieved cart items from localStorage:', cartData.items.length, 'items');
          return cartData.items;
        } else {
          console.warn('Cart items data is stale, removing...');
          localStorage.removeItem('stripeCartItems');
        }
      }
    }

    // Fallback: try to get from pending order data
    const savedOrderData = localStorage.getItem('pendingStripeOrder');
    if (savedOrderData) {
      const orderData: OrderData = JSON.parse(savedOrderData);
      if (orderData.cart && Array.isArray(orderData.cart) && orderData.cart.length > 0) {
        console.log('Retrieved cart items from order data fallback:', orderData.cart.length, 'items');
        return orderData.cart;
      }
    }

    console.warn('No cart items found in localStorage');
    return null;
  } catch (error) {
    console.error('Failed to retrieve cart items:', error);
    return null;
  }
};

/**
 * Clear all cart-related data from localStorage
 */
export const clearCartStorage = (): void => {
  try {
    localStorage.removeItem('stripeCartItems');
    localStorage.removeItem('pendingStripeOrder');
    console.log('Cart storage cleared successfully');
  } catch (error) {
    console.error('Failed to clear cart storage:', error);
  }
};

/**
 * Validate cart items structure
 */
export const validateCartItems = (items: any[]): boolean => {
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }

  return items.every(item => 
    item && 
    typeof item === 'object' && 
    item.product_id && 
    typeof item.quantity === 'number' && 
    item.quantity > 0
  );
};

/**
 * Get cart items with comprehensive error handling and validation
 */
export const getValidatedCartItems = (): { items: CartItem[] | null; error: string | null } => {
  try {
    const items = getCartItems();
    
    if (!items) {
      return {
        items: null,
        error: 'No cart items found. Please add items to cart and try again.'
      };
    }

    if (!validateCartItems(items)) {
      return {
        items: null,
        error: 'Invalid cart data found. Please refresh your cart and try again.'
      };
    }

    return {
      items,
      error: null
    };
  } catch (error) {
    return {
      items: null,
      error: 'Failed to load cart data. Please try again.'
    };
  }
};